/**
 * Service Sales - Client Management
 *
 * Client search, creation, and management functions specifically for
 * the service sales system. Provides optimized search and inline
 * client creation capabilities.
 *
 * @module services/sales/clientsService
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { validateCPF, validateCNPJ } from '@/lib/validation/brazilian-validators';
import {
  generateRegistrationCode,
  formatRegistrationCode,
  isValidRegistrationCode,
  validateRegistrationCode,
} from '@/utils/clientRegistrationCode';

// Simple fetchClients function for service sales
export async function fetchClients(): Promise<Client[]> {
  const { data, error } = await supabase
    .from("clients")
    .select("*")
    .eq("is_active", true)
    .order("name", { ascending: true });

  if (error) {
    console.error("Error fetching clients:", error);
    throw error;
  }

  return data || [];
}

export type Client = Database['public']['Tables']['clients']['Row'];
export type ClientInsert = Database['public']['Tables']['clients']['Insert'];
export type ClientUpdate = Database['public']['Tables']['clients']['Update'];

export interface ClientFormData {
  name: string;
  documentType: 'cpf' | 'cnpj';
  documentNumber: string; // Now required - matches database constraint
  registrationCode?: string | null;
  email?: string;
  phone?: string;
  address?: {
    street?: string;
    number?: string;
    complement?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  bankInfo?: {
    bankName?: string;
    agency?: string;
    account?: string;
    accountType?: 'corrente' | 'poupanca';
  };
  paymentTerms?: string;
  notes?: string;
  isActive?: boolean;
}

export interface ClientSearchResult {
  id: string;
  name: string;
  documentNumber: string;
  documentType: string;
  registrationCode?: string | null;
  email?: string;
  phone?: string;
  isActive: boolean;
  createdAt: string;
}

export interface ClientSearchFilters {
  search?: string;
  documentType?: 'cpf' | 'cnpj';
  registrationCode?: string;
  isActive?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: 'name' | 'created_at' | 'document_number' | 'registration_code';
  orderDirection?: 'asc' | 'desc';
}

export interface ClientValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Search clients with optimized queries for dropdown/select components
 */
export async function searchClients(
  searchTerm?: string,
  filters: ClientSearchFilters = {}
): Promise<ClientSearchResult[]> {
  try {
    let query = supabase
      .from('clients')
      .select(`
        id,
        name,
        document_number,
        document_type,
        registration_code,
        email,
        phone,
        is_active,
        created_at
      `)
      .eq('is_active', filters.isActive !== false)
      .order('name', { ascending: true });

    // Apply search filter
    if (searchTerm && searchTerm.trim().length > 0) {
      const cleanedSearch = searchTerm.trim();

      // Search by name, document number, registration code, email, or phone
      query = query.or(`
        name.ilike.%${cleanedSearch}%,
        document_number.ilike.%${cleanedSearch}%,
        registration_code.ilike.%${cleanedSearch}%,
        email.ilike.%${cleanedSearch}%,
        phone.ilike.%${cleanedSearch}%
      `);
    }

    // Apply registration code filter
    if (filters.registrationCode && filters.registrationCode.trim().length > 0) {
      const formattedCode = formatRegistrationCode(filters.registrationCode);
      query = query.eq('registration_code', formattedCode);
    }

    // Apply document type filter
    if (filters.documentType) {
      query = query.eq('document_type', filters.documentType);
    }

    // Apply active status filter
    if (filters.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }

    // Apply pagination
    const limit = filters.limit || 50;
    query = query.limit(limit);

    if (filters.offset) {
      query = query.range(filters.offset, filters.offset + limit - 1);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error searching clients:', error);
      throw new Error(`Failed to search clients: ${error.message}`);
    }

    return (data || []).map(client => ({
      id: client.id,
      name: client.name,
      documentNumber: client.document_number,
      documentType: client.document_type,
      registrationCode: client.registration_code,
      email: client.email,
      phone: client.phone,
      isActive: client.is_active,
      createdAt: client.created_at || '',
    }));

  } catch (error) {
    console.error('Error in searchClients:', error);
    throw error;
  }
}

/**
 * Get client details by ID with full information
 */
export async function getClientById(id: string): Promise<Client | null> {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      console.error('Error fetching client:', error);
      throw new Error(`Failed to fetch client: ${error.message}`);
    }

    return data;

  } catch (error) {
    console.error('Error in getClientById:', error);
    throw error;
  }
}

/**
 * Create a new client with validation
 */
export async function createClient(
  clientData: ClientFormData,
  userId: string
): Promise<Client> {
  try {
    // Validate client data
    const validation = validateClientData(clientData);
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    // Check for duplicate document number
    if (clientData.documentNumber) {
      await checkDuplicateDocument(clientData.documentNumber);
    }

    // Transform form data to database format
    // The database handles registration_code automatically via DEFAULT value
    const dbData: ClientInsert = {
      user_id: userId,
      name: clientData.name.trim(),
      document_type: clientData.documentType,
      document_number: cleanDocumentNumber(clientData.documentNumber), // Now required
      // registration_code is handled by database default value - don't set from frontend
      email: clientData.email?.trim() || null,
      phone: cleanPhoneNumber(clientData.phone),
      address_street: clientData.address?.street?.trim() || null,
      address_number: clientData.address?.number?.trim() || null,
      address_complement: clientData.address?.complement?.trim() || null,
      address_neighborhood: clientData.address?.neighborhood?.trim() || null,
      address_city: clientData.address?.city?.trim() || null,
      address_state: clientData.address?.state?.trim() || null,
      address_zip_code: cleanZipCode(clientData.address?.zipCode),
      bank_name: clientData.bankInfo?.bankName?.trim() || null,
      bank_agency: clientData.bankInfo?.agency?.trim() || null,
      bank_account: clientData.bankInfo?.account?.trim() || null,
      bank_account_type: clientData.bankInfo?.accountType || null,
      payment_terms: clientData.paymentTerms?.trim() || null,
      notes: clientData.notes?.trim() || null,
      is_active: clientData.isActive !== false,
    };

    const { data, error } = await supabase
      .from('clients')
      .insert([dbData])
      .select()
      .single();

    if (error) {
      console.error('Error creating client:', error);

      // Handle specific database errors
      if (error.code === '23505') {
        if (error.message.includes('clients_document_number_key')) {
          throw new Error('Este CPF/CNPJ já está cadastrado no sistema.');
        }
      }

      throw new Error(`Failed to create client: ${error.message}`);
    }

    return data;

  } catch (error) {
    console.error('Error in createClient:', error);
    throw error;
  }
}

/**
 * Update an existing client
 */
export async function updateClient(
  id: string,
  updates: Partial<ClientFormData>
): Promise<Client> {
  try {
    // Validate updates if document number is being changed
    if (updates.documentNumber) {
      const validation = validateClientData({
        name: updates.name || '',
        documentType: updates.documentType || 'cpf',
        documentNumber: updates.documentNumber,
      });

      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // Check for duplicate document number (excluding current client)
      await checkDuplicateDocument(updates.documentNumber, id);
    }

    // Validate registration code if being changed
    if (updates.registrationCode !== undefined) {
      if (updates.registrationCode) {
        const codeValidation = validateRegistrationCode(updates.registrationCode);
        if (!codeValidation.isValid) {
          throw new Error(`Código de registro inválido: ${codeValidation.errors.join(', ')}`);
        }
        // Check for uniqueness
        await checkDuplicateRegistrationCode(updates.registrationCode, id);
      }
    }

    const updateData: ClientUpdate = {
      updated_at: new Date().toISOString(),
    };

    if (updates.name !== undefined) updateData.name = updates.name.trim();
    if (updates.documentType !== undefined) updateData.document_type = updates.documentType;
    if (updates.documentNumber !== undefined) {
      updateData.document_number = updates.documentNumber ? cleanDocumentNumber(updates.documentNumber) : null;
    }
    if (updates.registrationCode !== undefined) {
      updateData.registration_code = updates.registrationCode ? formatRegistrationCode(updates.registrationCode) : null;
    }
    if (updates.email !== undefined) updateData.email = updates.email?.trim() || null;
    if (updates.phone !== undefined) updateData.phone = cleanPhoneNumber(updates.phone);
    if (updates.address?.street !== undefined) {
      updateData.address_street = updates.address.street.trim() || null;
    }
    if (updates.address?.number !== undefined) {
      updateData.address_number = updates.address.number.trim() || null;
    }
    if (updates.address?.complement !== undefined) {
      updateData.address_complement = updates.address.complement?.trim() || null;
    }
    if (updates.address?.neighborhood !== undefined) {
      updateData.address_neighborhood = updates.address.neighborhood?.trim() || null;
    }
    if (updates.address?.city !== undefined) {
      updateData.address_city = updates.address.city.trim() || null;
    }
    if (updates.address?.state !== undefined) {
      updateData.address_state = updates.address.state.trim() || null;
    }
    if (updates.address?.zipCode !== undefined) {
      updateData.address_zip_code = cleanZipCode(updates.address.zipCode);
    }
    if (updates.bankInfo?.bankName !== undefined) {
      updateData.bank_name = updates.bankInfo.bankName.trim() || null;
    }
    if (updates.bankInfo?.agency !== undefined) {
      updateData.bank_agency = updates.bankInfo.agency?.trim() || null;
    }
    if (updates.bankInfo?.account !== undefined) {
      updateData.bank_account = updates.bankInfo.account?.trim() || null;
    }
    if (updates.bankInfo?.accountType !== undefined) {
      updateData.bank_account_type = updates.bankInfo.accountType;
    }
    if (updates.paymentTerms !== undefined) {
      updateData.payment_terms = updates.paymentTerms?.trim() || null;
    }
    if (updates.notes !== undefined) {
      updateData.notes = updates.notes?.trim() || null;
    }
    if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

    const { data, error } = await supabase
      .from('clients')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating client:', error);

      if (error.code === '23505') {
        if (error.message.includes('clients_document_number_key')) {
          throw new Error('Este CPF/CNPJ já está cadastrado no sistema.');
        }
      }

      throw new Error(`Failed to update client: ${error.message}`);
    }

    return data;

  } catch (error) {
    console.error('Error in updateClient:', error);
    throw error;
  }
}

/**
 * Deactivate a client (soft delete)
 */
export async function deactivateClient(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('clients')
      .update({
        is_active: false,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) {
      console.error('Error deactivating client:', error);
      throw new Error(`Failed to deactivate client: ${error.message}`);
    }

  } catch (error) {
    console.error('Error in deactivateClient:', error);
    throw error;
  }
}

/**
 * Reactivate a client
 */
export async function reactivateClient(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('clients')
      .update({
        is_active: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) {
      console.error('Error reactivating client:', error);
      throw new Error(`Failed to reactivate client: ${error.message}`);
    }

  } catch (error) {
    console.error('Error in reactivateClient:', error);
    throw error;
  }
}

/**
 * Get client statistics for a user
 */
export async function getClientStats(userId: string): Promise<{
  total: number;
  active: number;
  inactive: number;
  recentlyCreated: number;
}> {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('is_active, created_at')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching client stats:', error);
      throw new Error(`Failed to fetch client stats: ${error.message}`);
    }

    const clients = data || [];
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return {
      total: clients.length,
      active: clients.filter(c => c.is_active).length,
      inactive: clients.filter(c => !c.is_active).length,
      recentlyCreated: clients.filter(c =>
        new Date(c.created_at || '') > thirtyDaysAgo
      ).length,
    };

  } catch (error) {
    console.error('Error in getClientStats:', error);
    throw error;
  }
}

/**
 * Validate client data with comprehensive checks
 */
export function validateClientData(
  clientData: ClientFormData
): ClientValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Name validation
  if (!clientData.name || clientData.name.trim().length < 3) {
    errors.push('Nome deve ter pelo menos 3 caracteres');
  } else if (clientData.name.trim().length > 100) {
    errors.push('Nome não pode ter mais de 100 caracteres');
  }

  // Document validation (now required)
  if (!clientData.documentNumber || clientData.documentNumber.trim().length === 0) {
    errors.push('CPF/CNPJ é obrigatório');
  } else {
    const cleanedDoc = cleanDocumentNumber(clientData.documentNumber);

    if (clientData.documentType === 'cpf') {
      if (!validateCPF(cleanedDoc)) {
        errors.push('CPF inválido');
      }
    } else if (clientData.documentType === 'cnpj') {
      if (!validateCNPJ(cleanedDoc)) {
        errors.push('CNPJ inválido');
      }
    }
  }

  // Email validation
  if (clientData.email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(clientData.email.trim())) {
      errors.push('Email inválido');
    }
  }

  // Phone validation
  if (clientData.phone) {
    const cleanedPhone = cleanPhoneNumber(clientData.phone);
    const phoneRegex = /^\d{10,11}$/;
    if (!phoneRegex.test(cleanedPhone)) {
      warnings.push('Formato de telefone pode estar incorreto');
    }
  }

  // Address validation
  if (clientData.address?.zipCode) {
    const cleanedZip = cleanZipCode(clientData.address.zipCode);
    const zipRegex = /^\d{8}$/;
    if (!zipRegex.test(cleanedZip)) {
      errors.push('CEP inválido');
    }
  }

  // Registration code validation (optional for form data)
  if (clientData.registrationCode) {
    const codeValidation = validateRegistrationCode(clientData.registrationCode);
    if (!codeValidation.isValid) {
      errors.push(...codeValidation.errors);
    }
    warnings.push(...codeValidation.warnings);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Check for duplicate document number
 */
async function checkDuplicateDocument(
  documentNumber?: string,
  excludeId?: string
): Promise<void> {
  try {
    if (!documentNumber) return;

    const cleanedDoc = cleanDocumentNumber(documentNumber);

    if (!cleanedDoc) return;

    let query = supabase
      .from('clients')
      .select('id')
      .eq('document_number', cleanedDoc);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query.limit(1);

    if (error) {
      console.error('Error checking duplicate document:', error);
      throw new Error(`Failed to check duplicate document: ${error.message}`);
    }

    if (data && data.length > 0) {
      throw new Error('Este CPF/CNPJ já está cadastrado no sistema.');
    }

  } catch (error) {
    if (error.message.includes('já está cadastrado')) {
      throw error;
    }
    console.error('Error in checkDuplicateDocument:', error);
    throw error;
  }
}

/**
 * Check for duplicate registration code
 */
async function checkDuplicateRegistrationCode(
  registrationCode?: string,
  excludeId?: string
): Promise<void> {
  try {
    if (!registrationCode) return;

    const formattedCode = formatRegistrationCode(registrationCode);

    let query = supabase
      .from('clients')
      .select('id')
      .eq('registration_code', formattedCode);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query.limit(1);

    if (error) {
      console.error('Error checking duplicate registration code:', error);
      throw new Error(`Failed to check duplicate registration code: ${error.message}`);
    }

    if (data && data.length > 0) {
      throw new Error('Este código de registro já está cadastrado no sistema.');
    }

  } catch (error) {
    if (error.message.includes('já está cadastrado')) {
      throw error;
    }
    console.error('Error in checkDuplicateRegistrationCode:', error);
    throw error;
  }
}

/**
 * Clean document number (remove formatting)
 */
function cleanDocumentNumber(documentNumber: string): string {
  return documentNumber.replace(/\D/g, '');
}

/**
 * Clean phone number (remove formatting)
 */
function cleanPhoneNumber(phone?: string): string | null {
  if (!phone) return null;
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length >= 10 ? cleaned : null;
}

/**
 * Clean ZIP code (remove formatting)
 */
function cleanZipCode(zipCode?: string): string | null {
  if (!zipCode) return null;
  const cleaned = zipCode.replace(/\D/g, '');
  return cleaned.length === 8 ? cleaned : null;
}

/**
 * Format client display name
 */
export function formatClientDisplayName(
  client: ClientSearchResult | Client,
  options: { includeRegistrationCode?: boolean; includeDocument?: boolean } = {}
): string {
  const { includeRegistrationCode = false, includeDocument = true } = options;
  const name = client.name;

  // Get formatted pieces
  const docNumber = client.document_number;
  const registrationCode = client.registration_code;

  const pieces: string[] = [name];

  if (includeRegistrationCode && registrationCode) {
    pieces.push(`Código: ${registrationCode}`);
  }

  if (includeDocument && docNumber) {
    const formattedDoc = formatDocumentNumber(docNumber);
    pieces.push(`(${formattedDoc})`);
  }

  return pieces.join(' ');
}

/**
 * Format document number for display
 */
function formatDocumentNumber(docNumber: string): string {
  const cleaned = cleanDocumentNumber(docNumber);

  if (cleaned.length === 11) {
    // CPF: XXX.XXX.XXX-XX
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (cleaned.length === 14) {
    // CNPJ: XX.XXX.XXX/XXXX-XX
    return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  return docNumber;
}

/**
 * Search clients specifically by registration code
 */
export async function searchClientByRegistrationCode(
  registrationCode: string
): Promise<Client | null> {
  try {
    const formattedCode = formatRegistrationCode(registrationCode);

    if (!formattedCode) {
      return null;
    }

    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('registration_code', formattedCode)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      console.error('Error searching client by registration code:', error);
      throw new Error(`Failed to search client: ${error.message}`);
    }

    return data;

  } catch (error) {
    console.error('Error in searchClientByRegistrationCode:', error);
    throw error;
  }
}

/**
 * Get suggested clients based on recent activity
 */
export async function getSuggestedClients(
  userId: string,
  limit: number = 10
): Promise<ClientSearchResult[]> {
  try {
    // This could be enhanced to include clients from recent sales,
    // most frequently used clients, etc.
    const { data, error } = await supabase
      .from('clients')
      .select(`
        id,
        name,
        document_number,
        document_type,
        registration_code,
        email,
        phone,
        is_active,
        created_at
      `)
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching suggested clients:', error);
      throw new Error(`Failed to fetch suggested clients: ${error.message}`);
    }

    return (data || []).map(client => ({
      id: client.id,
      name: client.name,
      documentNumber: client.document_number,
      documentType: client.document_type,
      registrationCode: client.registration_code,
      email: client.email,
      phone: client.phone,
      isActive: client.is_active,
      createdAt: client.created_at || '',
    }));

  } catch (error) {
    console.error('Error in getSuggestedClients:', error);
    throw error;
  }
}